import { Input } from '@/components/ui/input';
import { SectionHeading } from '../common/SectionHeading';
import { useTranslation } from '@hooks/useTranslation';
import { FormFieldRow } from '../Form/FormFieldRow';
import { OptionSelect } from '../Form/OptionSelect';
import type { SlingPosition } from '@api/users';

interface SlingFieldsProps {
  positions: SlingPosition[];
  hidden: boolean;
  headingSize: 'sm' | 'md';
  className?: string;
}

/** Links the person to Sling. Both fields empty means not linked. */
export function SlingFields({ positions, hidden, headingSize, className }: SlingFieldsProps) {
  const { t, getSection } = useTranslation();
  const placeholders = getSection<Record<string, string>>('form.placeholders') || {};
  const userIdLabel = t('form.labels.sling_user_id');
  const positionLabel = t('form.labels.sling_position_id');

  return (
    <div hidden={hidden} className={className}>
      <SectionHeading as="h4" rule={false} size={headingSize}>
        {t('common.sections.sling')}
      </SectionHeading>

      <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-2">
        <FormFieldRow name="sling_user_id" label={userIdLabel} marker="optional" markerLabel={t('form.optional')}>
          {(field, { invalid }) => (
            <Input
              value={field.state.value as string}
              onChange={(e) => field.handleChange(e.target.value.replace(/\D/g, ''))}
              onBlur={field.handleBlur}
              placeholder={placeholders.sling_user_id ?? ''}
              inputMode="numeric"
              autoComplete="off"
              variant="form"
              aria-label={userIdLabel}
              aria-invalid={invalid || undefined}
            />
          )}
        </FormFieldRow>

        <FormFieldRow name="sling_position_id" label={positionLabel} marker="optional" markerLabel={t('form.optional')}>
          {(field, { invalid }) => (
            <OptionSelect
              field={field}
              options={positions.map(({ id, name }) => ({ value: id, label: name }))}
              label={positionLabel}
              placeholder={placeholders.select_sling_position}
              invalid={invalid}
              clearLabel={t('form.labels.sling_not_linked')}
            />
          )}
        </FormFieldRow>
      </div>
    </div>
  );
}
